/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  EmptyState,
  EmptyStateProps,
  ICON_LOGS_WITH_LIST,
  ICON_LOGS_WITH_SEARCH,
  SPINNER_TYPE_REGULAR,
  SdsIcon,
  SdsIconProps,
  Spinner,
  TOOLTIP_POSITION_RIGHT,
  Table,
  TableCell,
  TableContent,
  TableProps,
  Tooltip,
} from '@squiz/sds';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MONITORING_STRINGS } from '../../../../Constants/Strings.constants';
import { convertDatetime } from '../../../../Utilities/ConvertDatetime/ConvertDatetime';
import { getLogLevel } from '../../../../Utilities/GetLogLevel/GetLogLevel';
import { getLogLevelIcon } from '../../../../Utilities/GetLogLevelIcon/GetLogLevelIcon';
import { LOGS_TABLE_DATA_IDS } from './LogsTable.constants';
import { LogsTableProps } from './LogsTable.props';

/**
 * Table to view and filter logs.
 */
export const LogsTable = (props: LogsTableProps): JSX.Element => {
  const { t } = useTranslation();

  const { loading, logs, searchText } = props;

  // Labels
  const labels = useMemo(() => {
    return {
      date: t(MONITORING_STRINGS.date),
      message: t(MONITORING_STRINGS.message),
      noLogs: t(MONITORING_STRINGS.noLogs),
      noMatchingLogs: t(MONITORING_STRINGS.noMatchingLogs),
      service: t(MONITORING_STRINGS.service),
      tags: t(MONITORING_STRINGS.tags),
      userId: t(MONITORING_STRINGS.userId),
    };
  }, [t]);

  if (loading) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  if (!logs || logs.length === 0) {
    const emptyStateProps: EmptyStateProps = {
      caption: !searchText ? labels.noLogs : labels.noMatchingLogs,
      dataId: LOGS_TABLE_DATA_IDS.emptyState,
      svg: !searchText ? ICON_LOGS_WITH_LIST : ICON_LOGS_WITH_SEARCH,
    };
    return <EmptyState {...emptyStateProps} />;
  }

  // Table setup
  const tableColumns: Array<TableCell> = [
    {
      content: ``,
      key: `type`,
    },
    {
      content: labels.date,
      key: `date`,
    },
    {
      content: labels.service,
      key: `service`,
    },
    {
      content: labels.userId,
      key: `userId`,
    },
    {
      content: labels.tags,
      key: `tags`,
    },
    {
      content: labels.message,
      key: `message`,
    },
  ];

  const tableContent: TableContent = {
    columns: tableColumns,
    rows: logs.map((log) => {
      const { level, message, service, tags, timestamp, userid } = log;

      const logType = getLogLevel(level);
      const logTypeIcon = getLogLevelIcon(level);
      const logTypeProps: SdsIconProps = {
        classes: `logs__type logs__type--${logType}`,
        svg: logTypeIcon,
      };

      // Convert to friendly text
      const tooltipLabel = logType[0].toUpperCase() + logType.slice(1);

      return {
        date: convertDatetime(timestamp),
        message: message || `-`,
        service,
        tags: tags || `-`,
        type: (
          <div>
            <Tooltip
              data-id={LOGS_TABLE_DATA_IDS.logLevelTooltip}
              label={tooltipLabel}
              position={TOOLTIP_POSITION_RIGHT}
            >
              <SdsIcon {...logTypeProps} />
            </Tooltip>
          </div>
        ),
        userId: userid || `-`,
      };
    }),
  };

  const tableProps: TableProps = {
    classes: `logs__table`,
    isPaginated: false,
    isSlim: true,
    tableContent: tableContent,
  };

  return <Table {...tableProps} data-id={LOGS_TABLE_DATA_IDS.logsTable} />;
};
